/** What a class server address is (STORY-110, STORY-136): pure string work, no DOM, so it is
 * tested on node and shared by the Student app's server box and the scanned join link.
 *
 * An address is an origin plus an optional path prefix, never with a trailing slash:
 * `http://192.168.1.27:7420` (the Teacher Computer app on the room network),
 * `https://classes.example.edu`, or `https://classes.example.edu/lessons` when the server is
 * mounted under a path on a host whose root belongs to another application. The prefix is
 * part of the address because everything the server serves lives under it: the join page,
 * the gateway, the health route. Dropping it, as the first version did, sends the student to
 * the other application, which answers 200 with its own page. */

const SCHEME = /^https?:\/\//i;

function fromUrl(u: URL): string | null {
  if (!u.hostname) return null;
  if (u.protocol !== "http:" && u.protocol !== "https:") return null;
  return u.origin + u.pathname.replace(/\/+$/, "");
}

/** "192.168.1.27:7420", "http://host", "https://classes.example.edu/lessons/" → an address, or
 * null. Typed by a student from the board; a scheme is optional and http is assumed. */
export function normaliseServer(input: string): string | null {
  const raw = input.trim();
  if (!raw) return null;
  // another scheme is not a typo for http: "ftp://x" must not become "http://ftp://x"
  if (raw.includes("://") && !SCHEME.test(raw)) return null;
  try {
    const u = new URL(SCHEME.test(raw) ? raw : `http://${raw}`);
    u.search = "";
    u.hash = "";
    return fromUrl(u);
  } catch {
    return null;
  }
}

/** The address of the server a join link points at, or null when the text is a bare code.
 * `https://classes.example.edu/lessons/j/AB12` → `https://classes.example.edu/lessons`: the
 * prefix is whatever comes before the join route. */
export function serverFromLink(text: string): string | null {
  let u: URL;
  try {
    u = new URL(text.trim());
  } catch {
    return null;
  }
  if (u.protocol !== "http:" && u.protocol !== "https:") return null;
  const path = u.pathname;
  const at = path.search(/\/j\/[^/]/);
  if (at >= 0) return u.origin + path.slice(0, at);
  // a link to the app's root, with or without a slash, names the server itself
  return u.origin + path.replace(/\/+$/, "");
}

/** How the address is shown back: the host, and the prefix when there is one, without the
 * scheme a student did not type. */
export function serverLabel(address: string): string {
  try {
    const u = new URL(address);
    return u.host + u.pathname.replace(/\/+$/, "");
  } catch {
    return address;
  }
}

/** The health routes to try, in order: the gateway's, then the local host's (which has no
 * prefix and answers /v1/health itself). */
export function healthCandidates(address: string): string[] {
  return [`${address}/session/v1/health`, `${address}/v1/health`];
}

/** "Yes, a class server": a JSON body from the session service. A 200 of HTML is what the
 * other application on a shared host answers, and it is not us. */
export function isSessionHealth(body: unknown): boolean {
  if (!body || typeof body !== "object") return false;
  const b = body as { ok?: unknown; service?: unknown };
  return b.ok === true && (b.service === undefined || b.service === "session");
}
